import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiChevronDown,
  FiChevronRight,
  FiSearch,
  FiLink,
  FiX,
  FiSave,
  FiEye,
  FiUpload,
  FiBarChart2,
  FiClock,
  FiLayers,
} from "react-icons/fi";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";
import {
  getWebTasks,
  createWebTask,
  updateWebTask,
  toggleWebTaskStatus,
  deleteWebTask,
  resolveWebTaskLogoUrl,
  getWebTaskChartData,
} from "../api/webTaskAdminApi";
import WebTaskActivityChart from "./components/WebTaskActivityChart";
import WebTaskTransactionHistory from "./components/WebTaskTransactionHistory";

const TASK_ID_RE = /^[a-z0-9_]{3,64}$/;
const MAX_TIERS = 5;
const EMPTY_TIER = { seconds: 0, clicks: 0, pages: 0, ep: 0 };

const blankForm = () => ({
  taskId: "",
  title: "",
  description: "",
  url: "",
  logoUrl: "",
  tag: "",
  tagColor: "#FFB300",
  maxActiveSeconds: 90,
  cooldownSeconds: 0,
  dailyLimit: 10,
  isActive: true,
  tiers: [{ ...EMPTY_TIER, seconds: 30, ep: 30 }],
});


const getFaviconFallback = (url) => {
  try {
    if (!url) return null;
    const domain = new URL(url.startsWith("http") ? url : `https://${url}`).hostname;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`;
  } catch {
    return null;
  }
};

const tierLabel = (t) => {
  const parts = [`${Number(t.seconds) || 0}s`];
  if (Number(t.clicks) > 0) parts.push(`${Number(t.clicks)} Clicks`);
  if (Number(t.pages) > 0) parts.push(`${Number(t.pages)} Pages`);
  return `${parts.join(" + ")} → ${Number(t.ep) || 0} EP`;
};

/** Client-side validation mirroring §4.2 server rules */
const validateForm = (form) => {
  const errors = {};
  if (!form.taskId || !TASK_ID_RE.test(form.taskId)) {
    errors.taskId =
      "3–64 chars, lowercase letters, digits and underscores only (e.g. partner_quizzop)";
  }
  if (!form.title || !form.title.trim()) errors.title = "Title is required";
  if (!/^https?:\/\//i.test(form.url || "")) {
    errors.url = "URL must start with http:// or https://";
  }

  // Tier rules (ascending seconds, EP > 0, seconds ≤ maxActiveSeconds) are
  // enforced server-side only (§4.2) — no client-side tier validation.
  return errors;
};

/* ---------------- Tiers Builder (max 5 rows) ---------------- */
const TiersBuilder = ({ tiers, onChange, errors, maxActiveSeconds }) => {
  const updateTier = (idx, key, value) => {
    const next = tiers.map((t, i) =>
      i === idx ? { ...t, [key]: value } : t
    );
    onChange(next);
  };
  const removeTier = (idx) => {
    onChange(tiers.filter((_, i) => i !== idx));
  };
  const addTier = () => {
    if (tiers.length >= MAX_TIERS) return;
    onChange([...tiers, { ...EMPTY_TIER }]);
  };

  return (
    <div className="space-y-2">
      {tiers.map((tier, idx) => (
        <div key={idx} className="flex items-center gap-2">
          {[
            { key: "seconds", ph: "s" },
            { key: "clicks", ph: "clicks" },
            { key: "pages", ph: "pages" },
            { key: "ep", ph: "EP" },
          ].map(({ key, ph }) => (
            <input
              key={key}
              type="number"
              min="0"
              placeholder={ph}
              value={tier[key] ?? ""}
              onChange={(e) => updateTier(idx, key, e.target.value)}
              title={
                key === "seconds"
                  ? "Seconds (ascending)"
                  : key
              }
              className="w-full min-w-0 bg-white border border-gray-300 rounded-lg text-xs px-2 py-2 outline-none focus:border-black focus:ring-1 focus:ring-black text-center font-mono"
            />
          ))}
          <button
            type="button"
            onClick={() => removeTier(idx)}
            disabled={tiers.length <= 1}
            className="shrink-0 p-2 text-gray-400 hover:text-red-500 disabled:opacity-30 transition cursor-pointer"
            title="Remove tier"
          >
            <FiX size={14} />
          </button>
        </div>
      ))}
      {tiers.length > 0 && (
        <div className="flex gap-2 text-[10px] text-gray-400 uppercase tracking-wide font-bold">
          <span className="w-1/4">Seconds</span>
          <span className="w-1/4">Clicks</span>
          <span className="w-1/4">Pages</span>
          <span className="w-1/4">EP</span>
          <span className="w-8" />
        </div>
      )}
      {errors && <p className="text-xs text-red-500 font-medium">{errors}</p>}
      <button
        type="button"
        onClick={addTier}
        disabled={tiers.length >= MAX_TIERS}
        className="text-xs font-semibold text-black border border-dashed border-gray-400 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-40 transition cursor-pointer"
      >
        + Add Tier ({tiers.length}/{MAX_TIERS})
      </button>
      <p className="text-[11px] text-gray-400">
        Tier matches when a session reaches at least one tier's requirements — the
        highest matched tier is settled. Seconds must ascend. AdSense policy: <b>clicks = human taps on partner
        content only</b>, never ad clicks.
      </p>
    </div>
  );
};

/* ---------------- Create / Edit Modal ---------------- */
const TaskModal = ({ open, initial, onClose, onSaved }) => {
  const isEdit = Boolean(initial?.taskId && initial.__editing);
  const [form, setForm] = useState(blankForm());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null); // File to upload (preview via object URL)
  const [logoPreview, setLogoPreview] = useState(null); // object URL for the picked file

  useEffect(() => {
    if (open) {
      setErrors({});
      setLogoFile(null);
      setLogoPreview(null);
      setForm(
        initial?.__editing
          ? {
              ...blankForm(),
              ...initial,
              tiers: (initial.tiers || []).map((t) => ({ ...EMPTY_TIER, ...t })),
            }
          : blankForm()
      );
    }
  }, [open, initial]);

  // Revoke any pending object URL on unmount / when the file changes
  useEffect(() => {
    return () => {
      if (logoPreview) URL.revokeObjectURL(logoPreview);
    };
  }, [logoPreview]);

  const pickLogoFile = (file) => {
    if (!file || !file.size) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo must be under 2 MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Logo must be an image file (PNG, JPG, SVG, …)");
      return;
    }
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const clearLogo = () => {
    if (logoPreview) URL.revokeObjectURL(logoPreview);
    setLogoFile(null);
    setLogoPreview(null);
    setForm((f) => ({ ...f, logoUrl: "" }));
  };

  const set = (key, value) =>
    setForm((f) => ({ ...f, [key]: value === "" ? undefined : value }));

  const submit = async () => {
    const tiers = (form.tiers || []).map((t) => ({
      seconds: Number(t.seconds) || 0,
      clicks: Number(t.clicks) || 0,
      pages: Number(t.pages) || 0,
      ep: Number(t.ep) || 0,
    }));

    const maxTierSec = tiers.reduce(
      (max, t) => Math.max(max, Number(t.seconds) || 0),
      0
    );
    const maxActiveSeconds = Math.max(
      Number(form.maxActiveSeconds) || 0,
      maxTierSec,
      30
    );

    // Only send the documented §4.2 fields (no stray __editing/id/stats)
    const clean = {
      taskId: String(form.taskId || "").trim().toLowerCase(),
      title: String(form.title || "").trim(),
      description: String(form.description || "").trim(),
      url: String(form.url || "").trim(),
      // Uploaded file takes precedence; otherwise keep the typed logo URL
      logoUrl: logoFile ? "" : String(form.logoUrl || "").trim(),
      tag: String(form.tag || "").trim(),
      tagColor: String(form.tagColor || "").trim(),
      maxActiveSeconds,
      cooldownSeconds: 0,
      dailyLimit: Number(form.dailyLimit) || 0,
      isActive: !!form.isActive,
      tiers,
    };

    const errs = validateForm(clean);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    setSaving(true);
    try {
      if (isEdit) {
        await updateWebTask(initial.id ?? initial.taskId, clean, logoFile);
        toast.success(`Web task "${clean.title}" updated`);
      } else {
        await createWebTask(clean, logoFile);
        toast.success(`Web task "${clean.taskId}" created`);
      }
      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to save web task");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-white border border-gray-300 rounded-lg text-sm px-3 py-2.5 outline-none focus:border-black focus:ring-1 focus:ring-black transition";
  const labelCls =
    "block text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1";

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-xs" onClick={onClose} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-4 border border-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div>
                <h3 className="text-lg font-black text-gray-900">
                  {isEdit ? "Edit Web Task" : "New Web Task"}
                </h3>
                <p className="text-xs text-gray-400">
                  {isEdit
                    ? `Editing ${initial.taskId}`
                    : "Partner website task with EP reward tiers"}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-black rounded-lg hover:bg-gray-100 transition cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Task ID (slug) *</label>
                  <input
                    value={form.taskId}
                    onChange={(e) => set("taskId", e.target.value)}
                    placeholder="partner_quizzop"
                    disabled={isEdit}
                    className={`${inputCls} font-mono text-xs disabled:opacity-50 disabled:bg-gray-50 ${
                      errors.taskId ? "border-red-400" : ""
                    }`}
                  />
                  {errors.taskId && (
                    <p className="text-[11px] text-red-500 mt-1">{errors.taskId}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Title *</label>
                  <input
                    value={form.title}
                    onChange={(e) => set("title", e.target.value)}
                    placeholder="Quizzop Trivia Arena"
                    className={`${inputCls} ${errors.title ? "border-red-400" : ""}`}
                  />
                  {errors.title && (
                    <p className="text-[11px] text-red-500 mt-1">{errors.title}</p>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Description</label>
                  <input
                    value={form.description}
                    onChange={(e) => set("description", e.target.value)}
                    placeholder="Answer trivia questions & earn EP"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Partner URL *</label>
                  <input
                    value={form.url}
                    onChange={(e) => set("url", e.target.value)}
                    placeholder="https://partner.example.com/?id=ZETA"
                    className={`${inputCls} ${errors.url ? "border-red-400" : ""}`}
                  />
                  {errors.url && (
                    <p className="text-[11px] text-red-500 mt-1">{errors.url}</p>
                  )}
                </div>
                <div>
                  <label className={labelCls}>Logo URL</label>
                  <input
                    value={form.logoUrl}
                    onChange={(e) => {
                      set("logoUrl", e.target.value);
                      if (logoFile) clearLogo(); // switching back to URL mode
                    }}
                    placeholder="https://partner.example.com/favicon.ico"
                    className={inputCls}
                  />

                  {/* Logo upload */}
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center gap-3">
                      <label className="shrink-0 flex items-center gap-2 text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 hover:border-black transition cursor-pointer">
                        <FiUpload size={14} />
                        {logoFile ? "Change Logo" : "Upload Logo"}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => pickLogoFile(e.target.files?.[0] || null)}
                        />
                      </label>

                      {(logoPreview || form.logoUrl) && (
                        <div className="flex items-center gap-2 min-w-0">
                          <img
                            src={logoPreview || resolveWebTaskLogoUrl(form.logoUrl) || getFaviconFallback(form.url)}
                            alt="logo preview"
                            className="h-10 w-10 rounded-lg object-contain bg-gray-50 border border-gray-200 shrink-0"
                          />
                          <button
                            type="button"
                            onClick={clearLogo}
                            className="p-1.5 text-gray-400 hover:text-red-500 transition cursor-pointer shrink-0"
                            title="Remove logo"
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      )}
                    </div>

                    {(logoPreview || form.logoUrl) && (
                      <p
                        className="text-[10px] text-gray-400 truncate max-w-full"
                        title={logoFile ? logoFile.name : "Current logo URL"}
                      >
                        {logoFile ? `Selected: ${logoFile.name}` : "Using logo URL above"}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-400">
                      {logoFile
                        ? "Upload will replace the URL above · PNG / JPG / SVG, up to 2 MB"
                        : "PNG / JPG / SVG, up to 2 MB"}
                    </p>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Tag</label>
                  <input
                    value={form.tag}
                    onChange={(e) => set("tag", e.target.value)}
                    placeholder="QUIZ & CLICKS"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Tag Color</label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={form.tagColor || "#FFB300"}
                      onChange={(e) => set("tagColor", e.target.value)}
                      className="h-10 w-12 rounded-lg border border-gray-300 cursor-pointer"
                    />
                    <input
                      value={form.tagColor}
                      onChange={(e) => set("tagColor", e.target.value)}
                      placeholder="#FFB300"
                      className={`${inputCls} flex-1 font-mono text-xs`}
                    />
                  </div>
                </div>
              </div>

              {/* Limits */}
              <div>
                <label className={labelCls}>Limits</label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <input
                      type="number"
                      min="0"
                      value={form.maxActiveSeconds}
                      onChange={(e) => set("maxActiveSeconds", e.target.value)}
                      className={inputCls}
                      placeholder="Max active sec"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Max Active Seconds</p>
                  </div>
                  <div>
                    <input
                      type="number"
                      min="0"
                      value={form.dailyLimit}
                      onChange={(e) => set("dailyLimit", e.target.value)}
                      className={inputCls}
                      placeholder="Daily limit"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">
                      Daily Limit (0 = unlimited)
                    </p>
                  </div>
                </div>
              </div>

              {/* Tiers */}
              <div>
                <label className={labelCls}>Reward Tiers (seconds / clicks / pages → EP) *</label>
                <TiersBuilder
                  tiers={form.tiers}
                  onChange={(tiers) => {
                    const maxTierSec = (tiers || []).reduce(
                      (max, t) => Math.max(max, Number(t.seconds) || 0),
                      0
                    );
                    setForm((f) => ({
                      ...f,
                      tiers,
                      maxActiveSeconds: Math.max(Number(f.maxActiveSeconds) || 0, maxTierSec),
                    }));
                  }}
                  errors={errors.tiers}
                  maxActiveSeconds={form.maxActiveSeconds}
                />
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!form.isActive}
                    onChange={(e) => set("isActive", e.target.checked)}
                    className="h-4 w-4 accent-black"
                  />
                  Active immediately after save
                </label>
                <div className="flex items-center gap-2 text-[10px] text-gray-400">
                  <FiEye size={12} /> AdSense-safe: rewards never tied to ad clicks
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-semibold text-gray-600 border border-gray-200 rounded-xl hover:bg-white transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="px-5 py-2.5 text-sm font-bold text-black bg-[#ffff00] rounded-xl hover:bg-[#f5f500] disabled:opacity-50 transition flex items-center gap-2 cursor-pointer"
              >
                <FiSave size={15} />
                {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Task"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ---------------- Main Page ---------------- */
export default function WebTasks() {
  const [activeModule, setActiveModule] = useState("chart"); // "chart" | "history" | "tasks"
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [historyTaskId, setHistoryTaskId] = useState("");

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [pagination, setPagination] = useState({ totalItems: 0, currentPage: 1, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [limit] = useState(50);

  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(""); // "" | "true" | "false"
  const [expandedTiers, setExpandedTiers] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [busyTaskId, setBusyTaskId] = useState(null);

  const loadChart = useCallback(async () => {
    setChartLoading(true);
    try {
      const res = await getWebTaskChartData({ days: 30 });
      if (res?.data) {
        setChartData(res.data);
      }
    } catch (err) {
      console.warn("Chart data load error:", err);
    } finally {
      setChartLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChart();
  }, [loadChart]);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const res = await getWebTasks({
          page,
          limit,
          active: activeFilter === "" ? undefined : activeFilter === "true",
          search: search || undefined,
        });
        const data = res?.data || res || {};
        setTasks(data.webTasks || []);
        setPagination(data.pagination || { totalItems: 0, currentPage: page, totalPages: 1 });
      } catch (err) {
        console.error("Failed to load web tasks:", err);
        setTasks([]);
        toast.error(err.message || "Failed to load web tasks");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [page, limit, activeFilter, search]
  );

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = () => {
    setPage(1);
    load();
  };

  const handleToggle = async (task) => {
    setBusyTaskId(task.taskId);
    try {
      await toggleWebTaskStatus(task.id ?? task.taskId, !task.isActive);
      toast.success(`${task.title || task.taskId} is now ${task.isActive ? "disabled" : "active"}`);
      load(true);
    } catch (err) {
      toast.error(err.message || "Failed to toggle task");
    } finally {
      setBusyTaskId(null);
    }
  };

  const handleDelete = async (task) => {
    if (
      !window.confirm(
        `Disable "${task.title || task.taskId}"? Soft delete — session history and EP audit trail are preserved.`
      )
    ) {
      return;
    }
    setBusyTaskId(task.taskId);
    try {
      await deleteWebTask(task.id ?? task.taskId);
      toast.success(`${task.taskId} disabled (soft delete)`);
      load(true);
    } catch (err) {
      toast.error(err.message || "Failed to delete task");
    } finally {
      setBusyTaskId(null);
    }
  };

  const openEdit = (task) => {
    setEditingTask({ ...task, __editing: true });
    setModalOpen(true);
  };

  const busy = (task) => busyTaskId === task.taskId;

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />

        <div className="p-6 sm:p-8 space-y-6 w-full">
          {/* Breadcrumbs */}
          <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium flex-wrap">
            <span className="text-gray-900 font-bold bg-[#ffffcc] px-2 py-0.5 rounded">
              Web Tasks
            </span>
            <span className="text-gray-400">
              Partner site tasks · EP reward tiers · session audit
            </span>
          </div>

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-black text-gray-900">Web Tasks</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Track today's total users &amp; completed tasks, audit session transactions, and manage partner websites.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setEditingTask(null);
                  setModalOpen(true);
                }}
                className="px-4 py-2.5 bg-black text-[#ffff00] text-xs font-bold rounded-xl hover:bg-gray-800 transition flex items-center gap-2 cursor-pointer shadow-xs"
              >
                <FiPlus size={16} /> New Task
              </button>
            </div>
          </div>

          {/* MODULE SELECTOR TABS */}
          <div className="flex flex-wrap items-center justify-between gap-4 bg-white border border-gray-200 rounded-2xl p-2 shadow-xs">
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setActiveModule("chart")}
                className={`px-5 py-2.5 text-xs font-black rounded-xl transition flex items-center gap-2 cursor-pointer ${
                  activeModule === "chart"
                    ? "bg-black text-[#ffff00] shadow-sm"
                    : "text-gray-600 hover:text-black hover:bg-gray-100"
                }`}
              >
                <FiBarChart2 size={16} />
                <span>Today's Activity &amp; Chart</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveModule("history")}
                className={`px-5 py-2.5 text-xs font-black rounded-xl transition flex items-center gap-2 cursor-pointer ${
                  activeModule === "history"
                    ? "bg-black text-[#ffff00] shadow-sm"
                    : "text-gray-600 hover:text-black hover:bg-gray-100"
                }`}
              >
                <FiClock size={16} />
                <span>Transaction History</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveModule("tasks")}
                className={`px-5 py-2.5 text-xs font-black rounded-xl transition flex items-center gap-2 cursor-pointer ${
                  activeModule === "tasks"
                    ? "bg-black text-[#ffff00] shadow-sm"
                    : "text-gray-600 hover:text-black hover:bg-gray-100"
                }`}
              >
                <FiLayers size={16} />
                <span>Manage Web Tasks ({tasks.length})</span>
              </button>
            </div>

            <div className="text-xs text-gray-400 font-medium hidden sm:block pr-3">
              {activeModule === "chart" && "Live chart: Today's Users vs. Completed Tasks"}
              {activeModule === "history" && "Audit log of all user session claims & rewards"}
              {activeModule === "tasks" && "Configure partner URLs, tiers & limits"}
            </div>
          </div>

          {/* MODULE 1: CHART & ANALYTICS */}
          {activeModule === "chart" && (
            <WebTaskActivityChart
              chartData={chartData}
              loading={chartLoading}
              onRefresh={loadChart}
              onViewTaskHistory={(tid) => {
                setHistoryTaskId(tid);
                setActiveModule("history");
              }}
            />
          )}

          {/* MODULE 2: TRANSACTION HISTORY */}
          {activeModule === "history" && (
            <WebTaskTransactionHistory
              initialTaskId={historyTaskId}
            />
          )}

          {/* MODULE 3: MANAGE WEB TASKS TABLE */}
          {activeModule === "tasks" && (
            <div className="space-y-4">
              {/* Task Table Controls */}
              <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-gray-200 rounded-2xl p-4 shadow-xs">
                <div className="relative flex-1 min-w-[220px]">
                  <FiSearch
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                    placeholder="Search title or task ID…"
                    className="w-full bg-white border border-gray-300 rounded-xl pl-9 pr-3 py-2 text-xs outline-none focus:border-black focus:ring-1 focus:ring-black transition"
                  />
                </div>
                <select
                  value={activeFilter}
                  onChange={(e) => {
                    setActiveFilter(e.target.value);
                    setPage(1);
                    load();
                  }}
                  className="bg-white border border-gray-300 rounded-xl px-3 py-2 text-xs outline-none focus:border-black cursor-pointer font-medium"
                >
                  <option value="">All Status</option>
                  <option value="true">Active Only</option>
                  <option value="false">Disabled Only</option>
                </select>
                <button
                  onClick={handleSearch}
                  className="px-4 py-2 bg-white border border-gray-300 text-xs font-semibold rounded-xl hover:bg-gray-50 transition cursor-pointer"
                >
                  Search
                </button>
              </div>

              {/* Table */}
              {loading ? (
                <div className="flex items-center justify-center p-12 bg-white rounded-2xl border border-gray-100">
                  <div className="w-8 h-8 border-4 border-[#ffff00] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-xs text-gray-500 uppercase font-bold tracking-wider">
                      <th className="px-4 py-4">Task</th>
                      <th className="px-4 py-4">Tag</th>
                      <th className="px-4 py-4">Limits</th>
                      <th className="px-4 py-4">Tiers</th>
                      <th className="px-4 py-4">Sessions / EP</th>
                      <th className="px-4 py-4 text-center">Active</th>
                      <th className="px-4 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tasks.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-4 py-12 text-center text-gray-500">
                          No web tasks found{search ? ` for "${search}"` : ""}.
                        </td>
                      </tr>
                    ) : (
                      tasks.map((task) => {
                        const tiers = Array.isArray(task.tiers) ? task.tiers : [];
                        const stats = task.stats || {};
                        const isOpen = !!expandedTiers[task.taskId];
                        const tagColor = task.tagColor || "#FFB300";
                        return (
                          <React.Fragment key={task.id ?? task.taskId}>
                            <tr
                              className={`hover:bg-gray-50 transition-colors ${
                                task.isActive ? "" : "opacity-60"
                              }`}
                            >
                              <td className="px-4 py-4">
                                <div className="flex items-center gap-3">
                                  {(() => {
                                    const logo = resolveWebTaskLogoUrl(task.logoUrl) || getFaviconFallback(task.url);
                                    return (
                                      <>
                                        {logo && (
                                          <img
                                            src={logo}
                                            alt=""
                                            className="h-9 w-9 rounded-lg object-contain bg-gray-50 border border-gray-200 p-0.5"
                                            onError={(e) => {
                                              e.target.onerror = null;
                                              e.target.style.display = "none";
                                              if (e.target.nextSibling) {
                                                e.target.nextSibling.style.display = "flex";
                                              }
                                            }}
                                          />
                                        )}
                                        <div
                                          className="h-9 w-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-300"
                                          style={{ display: logo ? "none" : "flex" }}
                                        >
                                          <FiLink size={15} />
                                        </div>
                                      </>
                                    );
                                  })()}
                                  <div className="min-w-0">
                                    <div className="font-semibold text-gray-900 truncate max-w-[220px]">
                                      {task.title || task.taskId}
                                    </div>
                                    <div className="text-[11px] font-mono text-gray-400 truncate max-w-[220px]">
                                      {task.taskId}
                                    </div>
                                    {task.url && (
                                      <a
                                        href={task.url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] text-gray-400 hover:text-black underline truncate block max-w-[220px]"
                                      >
                                        {task.url}
                                      </a>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <span
                                  className="inline-block text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-md text-black"
                                  style={{
                                    backgroundColor: `${tagColor}55`,
                                    border: `1px solid ${tagColor}`,
                                  }}
                                >
                                  {task.tag || "—"}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-xs text-gray-500 font-mono">
                                <div>{task.maxActiveSeconds}s max</div>
                                <div>{task.dailyLimit === 0 ? "∞ daily" : `${task.dailyLimit} daily`}</div>
                              </td>
                              <td className="px-4 py-4">
                                <button
                                  onClick={() =>
                                    setExpandedTiers((prev) => ({
                                      ...prev,
                                      [task.taskId]: !prev[task.taskId],
                                    }))
                                  }
                                  className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-black transition cursor-pointer"
                                >
                                  {isOpen ? (
                                    <FiChevronDown size={13} />
                                  ) : (
                                    <FiChevronRight size={13} />
                                  )}
                                  {tiers.length} Tiers
                                </button>
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-xs space-y-0.5">
                                  <div>
                                    <b className="font-mono text-gray-900">
                                      {Number(stats.totalSessions || 0).toLocaleString()}
                                    </b>{" "}
                                    <span className="text-gray-400">sessions</span>
                                  </div>
                                  <div>
                                    <b className="font-mono text-gray-900">
                                      {Number(stats.completedSessions || 0).toLocaleString()}
                                    </b>{" "}
                                    <span className="text-gray-400">completed</span>
                                  </div>
                                  <div>
                                    <b className="font-mono text-green-700">
                                      {Number(stats.totalEpAwarded || 0).toLocaleString()}
                                    </b>{" "}
                                    <span className="text-gray-400">EP awarded</span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex justify-center">
                                  <button
                                    onClick={() => handleToggle(task)}
                                    disabled={busy(task)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition cursor-pointer disabled:opacity-50 ${
                                      task.isActive ? "bg-green-500" : "bg-gray-300"
                                    }`}
                                    title={task.isActive ? "Disable task" : "Enable task"}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 rounded-full bg-white transform transition ${
                                        task.isActive ? "translate-x-6" : "translate-x-1"
                                      }`}
                                    />
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex items-center justify-end gap-1.5">
                                  <Link
                                    to={`/web-task-audit?taskId=${encodeURIComponent(task.taskId)}`}
                                    title="View sessions for this task"
                                    className="p-2 text-gray-400 hover:text-black rounded-lg hover:bg-gray-100 transition"
                                  >
                                    <FiEye size={15} />
                                  </Link>
                                  <button
                                    onClick={() => openEdit(task)}
                                    disabled={busy(task)}
                                    className="p-2 text-gray-400 hover:text-black rounded-lg hover:bg-gray-100 transition cursor-pointer disabled:opacity-50"
                                    title="Edit task"
                                  >
                                    <FiEdit2 size={15} />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(task)}
                                    disabled={busy(task)}
                                    className="p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition cursor-pointer disabled:opacity-50"
                                    title="Soft delete (disable, keep history)"
                                  >
                                    <FiTrash2 size={15} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isOpen && tiers.length > 0 && (
                              <tr className="bg-gray-50/70">
                                <td colSpan="7" className="px-4 py-3">
                                  <div className="flex flex-wrap gap-2">
                                    {tiers.map((t, i) => (
                                      <span
                                        key={i}
                                        className="text-[11px] font-mono bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-gray-700"
                                      >
                                        {tierLabel(t)}
                                      </span>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalItems > 0 && (
                <div className="flex items-center justify-between px-4 py-4 border-t border-gray-200 bg-gray-50">
                  <span className="text-sm text-gray-500">
                    {pagination.totalItems} task
                    {pagination.totalItems === 1 ? "" : "s"}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      disabled={page <= 1 || loading}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-100 transition cursor-pointer"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-sm font-medium text-gray-700">
                      Page {page} of {Math.max(pagination.totalPages || 1, 1)}
                    </span>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(p + 1, Math.max(pagination.totalPages || 1, 1)))
                      }
                      disabled={page >= (pagination.totalPages || 1) || loading}
                      className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-gray-100 transition cursor-pointer"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Task Modal (Create / Edit) */}
      <TaskModal
        open={modalOpen}
          initial={editingTask}
          onClose={() => {
            setModalOpen(false);
            setEditingTask(null);
          }}
          onSaved={() => {
            setModalOpen(false);
            setEditingTask(null);
            load(true);
          }}
        />
        </div>
      </div>
    </div>
  );
}
